import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Кегль, при котором строка занимает всю ширину своего места — от левой
 * границы до правой.
 *
 * Считать кегль по числу знаков нельзя: у букв разная ширина, и при одной
 * и той же оценке короткое слово не дотягивается до краёв, а длинное
 * вылезает за зону печати. Поэтому строка измеряется, а не угадывается.
 *
 * Способ прямой: ставим опорный кегль, читаем настоящую ширину строки,
 * пересчитываем один раз. Подбора по шагам нет — ширина текста растёт
 * пропорционально кеглю, и одного деления достаточно.
 */

/** Опорный кегль для замера. Крупный: на мелком заметна ошибка округления. */
const PROBE_PX = 200;

/**
 * Сколько раз и как часто ждать, пока у места появится ширина.
 *
 * Надпись успевает появиться раньше, чем раскладывается изделие: пока
 * открыт диалог, зона печати нулевой ширины. Мерить там нечего, а повода
 * вернуться к замеру потом может и не быть.
 *
 * Таймером, а не `requestAnimationFrame`: в скрытой вкладке кадры
 * не идут вовсе, и ожидание никогда бы не закончилось. Это не выдумка,
 * проверено — в фоновой вкладке кадр не пришёл ни одного.
 */
const WAIT_TRIES = 40;
const WAIT_STEP_MS = 25;

interface Options {
  /**
   * Предел кегля в долях ширины места.
   *
   * Нужен для коротких слов: строка из двух букв, растянутая до краёв,
   * получает кегль почти во всю зону и не оставляет места остальному.
   */
  maxRatio?: number;
}

/**
 * Возвращает ссылку, которую вешают на сам текст. Место под строку —
 * его родитель: кегль считается по ширине родителя, а не по ширине
 * самого текста, которая от кегля и зависит.
 *
 * Два решения здесь приняты после разбора на живой вещи, и оба важны.
 *
 * **Ссылка обычная, а не функция.** Ссылку-функцию React зовёт только
 * на появлении и исчезновении узла, и поймать этот момент оказалось
 * ненадёжно. Ссылку-объект React проставляет в фазе фиксации всегда,
 * до layout-эффектов, — значит эффект ниже видит надпись в том же
 * обновлении, в котором она появилась.
 *
 * **Подбор идёт после каждой отрисовки, без списка зависимостей.**
 * Ключ «от чего пересчитывать» был, и из-за него всё и ломалось:
 * пересчёт шёл только на смену текста и шрифта, а ширина места меняется
 * и без них — от раскрытого диалога, от смены шага, от загрузки картинки
 * изделия. Замер дешёвый, пропущенный пересчёт виден на вещи.
 */
export function useFitText<T extends HTMLElement>({ maxRatio = 0.3 }: Options = {}) {
  const ref = useRef<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedRef = useRef<Element | null>(null);
  const timerRef = useRef(0);
  const triesRef = useRef(0);
  const fontRef = useRef('');

  /**
   * Подогнать кегль. Возвращает `false`, если мерить пока нечего —
   * надписи нет или у места нулевая ширина.
   */
  const fit = useCallback((): boolean => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return false;

    const available = box.clientWidth;
    if (!available) return false;

    // Замер идёт от опорного кегля, а не от текущего: иначе каждый
    // пересчёт отталкивался бы от прошлого результата и ошибка
    // накапливалась бы от правки к правке.
    el.style.fontSize = `${PROBE_PX}px`;

    // На время замера рамка — по содержимому. Без этого ширину пришлось бы
    // брать из scrollWidth, а он не меньше ширины рамки: строка, которая
    // при опорном кегле в неё уместилась, мерилась бы шириной рамки,
    // и кегль вышел бы заниженным.
    const width = el.style.width;
    el.style.width = 'max-content';
    const natural = el.getBoundingClientRect().width;
    el.style.width = width;

    if (!natural) return false;

    // Ширина берётся целиком, вместе с пробелом разрядки после последней
    // буквы. Вычесть его — значит растянуть буквы ровно на ширину места,
    // а строку вместе с пробелом сделать шире: центровка поделит излишек
    // пополам, и зона срежет край у первой и последней буквы. Лучше
    // недобрать полпроцента, чем обрезать букву.
    const size = Math.min((PROBE_PX * available) / natural, available * maxRatio);
    el.style.fontSize = `${size.toFixed(2)}px`;

    // Подобранный кегль — ещё и мера для соседей по композиции: подписи
    // и зазоры заданы в долях от надписи, а она каждый раз разного размера.
    box.style.setProperty('--ls-fit-size', `${size.toFixed(2)}px`);
    return true;
  }, [maxRatio]);

  /** Подогнать, а если мерить пока нечего — дождаться ширины. */
  const fitWhenReady = useCallback(() => {
    window.clearTimeout(timerRef.current);

    if (fit()) {
      triesRef.current = 0;
      return;
    }

    if (triesRef.current >= WAIT_TRIES) return;
    triesRef.current += 1;
    timerRef.current = window.setTimeout(fitWhenReady, WAIT_STEP_MS);
  }, [fit]);

  // Намеренно без списка зависимостей — см. описание хука.
  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.parentElement ?? null;

    // Место под надпись сменилось — перевесить слежение.
    // Ширина зоны меняется и без отрисовки: загрузилась картинка изделия,
    // потянули окно, закрылся диалог.
    if (box !== observedRef.current) {
      observerRef.current?.disconnect();
      observedRef.current = box;
      triesRef.current = 0;

      if (box) {
        const observer = new ResizeObserver(() => fitWhenReady());
        observer.observe(box);
        observerRef.current = observer;
      } else {
        observerRef.current = null;
      }
    }

    // Фирменная гарнитура подключена с `display=swap`: до её загрузки
    // строка набрана запасной, а у запасной другие метрики — Caveat,
    // к примеру, в полтора раза уже. Замер по запасной дал бы кегль
    // не для того шрифта, которым надпись в итоге покажут.
    if (el) {
      const look = getComputedStyle(el);
      const face = `${look.fontStyle} ${look.fontWeight} 16px ${look.fontFamily}`;
      if (face !== fontRef.current) {
        fontRef.current = face;
        void document.fonts
          .load(face, el.textContent ?? '')
          .then(fitWhenReady)
          .catch(fitWhenReady);
      }
    }

    fitWhenReady();
  });

  useEffect(() => {
    // Гарнитуру может дотянуть и что-то другое — соседний экран, другая
    // надпись. Событие приходит на каждую догруженную пачку шрифтов.
    const refit = () => fitWhenReady();
    document.fonts.addEventListener('loadingdone', refit);

    return () => document.fonts.removeEventListener('loadingdone', refit);
  }, [fitWhenReady]);

  // Слежение снимается только при уходе со страницы. Раньше это стояло
  // в уборке соседнего эффекта и могло оборвать наблюдение, оставив
  // надпись без единственного, что её чинило.
  useEffect(
    () => () => {
      window.clearTimeout(timerRef.current);
      observerRef.current?.disconnect();
    },
    [],
  );

  return ref;
}
